import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import MetaTooltip from "../medicines/MetaTooltip";
import {
  FamilyMarkIcon,
  HistoryGlyph,
  PlusIcon,
  ToolbarPlusIcon,
  TrashIcon,
} from "../labInvestigations/labIcons";
import DeleteConfirmDialog from "../common/DeleteConfirmDialog";
import DeletedEntriesModal from "./DeletedEntriesModal";
import FamilyHistoryService from "./FamilyHistoryService";
import { relationshipLabel, sortRelativesByRelationship } from "./data";
import { FAM_V2_ADD, FAM_V2_EDIT, FAM_V2_EDIT_CAPTURED, FAM_V2_REFRESH } from "./events";

/**
 * Family History — content column, backed by api-family-history-v2-service.
 * Px-matched to "Card · Family history (hero)" (3071:2703, 834×390).
 *
 *   • Empty state: "No family history recorded" + Add relative CTA.
 *   • Populated: 6-col table (RELATIONSHIP · CONDITION · DESCRIPTION ·
 *     ADDED BY · ADDED ON · actions). Clicking the relationship edits the
 *     row; the row trash soft-deletes (after a confirm dialog).
 *
 * This column owns no form. The header CTA raises FAM_V2_ADD and a
 * relationship click raises FAM_V2_EDIT with the row; both render in the
 * editor rail (3071:2760), which is the only place a relative is written.
 *
 * ADDED BY / ADDED ON are new COLUMNS on the board but the family-history
 * wire carries no audit fields today, so they are read defensively off
 * whatever the server sends and fall back to the board's own em-dash. No
 * value is synthesised client-side.
 *
 * The board's "Deleted entries" button is rendered for the px-match but is
 * inert: soft-deleted rows are filtered out server-side and there is no
 * undelete/list-deleted endpoint (see FamilyHistoryService header).
 */

// Comma-join the condition names; em-dash if none. Tolerates plain strings as
// well as {name} objects so a shape change on the wire can't blank the page.
const formatConditions = (cs) => {
  if (!Array.isArray(cs) || cs.length === 0) return "—";
  const names = cs.map((c) => (typeof c === "string" ? c.trim() : asText(c))).filter(Boolean);
  return names.length ? names.join(", ") : "—";
};

/**
 * Coerce whatever the list endpoint returned into a clean array of relative
 * objects. A malformed payload used to take the ENTIRE patient screen down —
 * `relatives` arriving as a keyed object made `.map` throw, and a null entry
 * threw on `.relationship`, in both cases with no error boundary above us.
 * Reproduced against the running app; this keeps the section rendering
 * whatever it can instead.
 */
const toRelativeList = (raw) => {
  let list = raw;
  if (!Array.isArray(list)) {
    list = list && typeof list === "object" ? Object.keys(list).map((k) => list[k]) : [];
  }
  return list.filter((r) => r && typeof r === "object");
};

/**
 * Render-safe text. React throws "Objects are not valid as a React child" for
 * anything object-shaped, which unmounts the whole tree — a white screen, not
 * a broken cell. Audit fields in particular are commonly sent as
 * `{id, name}` rather than a plain string, so every value that reaches JSX
 * goes through here first.
 */
const asText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return "";
  if (typeof v === "object") {
    const named = v.name || v.fullName || v.displayName || v.label || v.email;
    return typeof named === "string" ? named.trim() : "";
  }
  return "";
};

/**
 * ADDED BY / ADDED ON come off api-family-history-v2-service's own audit
 * fields (model.Relative). The backend enriches every row with an
 * `addedByName` display string (routed through docprofile / employees
 * with a 30-min in-process cache); when it's missing we fall back to
 * whatever the actor's shape carries so the column never blanks.
 */
const addedBy = (r) => {
  if (!r) return "";
  const enriched = r.addedByName || r.updatedByName;
  if (enriched && enriched.trim()) return enriched.trim();
  const a = r.addedBy || r.createdBy || r.updatedBy;
  if (!a) return "";
  if (typeof a === "string") return a.trim();
  if (a.who === "service") return "System";
  const label = a.who ? a.who[0].toUpperCase() + a.who.slice(1) : "";
  if (label && a.whoId) return `${label} · ${a.whoId}`;
  return label || "";
};

const addedOn = (r) => {
  const v = r && (r.addedAt || r.createdAt || r.addedOn || r.createdOn);
  return typeof v === "string" || typeof v === "number" ? v : "";
};

const Dash = () => <span className="fh3-dash">—</span>;

// Build a synthetic Relative-shaped row from a staged "add" captured item
// so it can slot into the same table as FH v2 rows. Mirrors the other
// v2 sections. Wave B first-cut: staged UPDATES aren't rendered.
const stagedAddToRelativeRow = (item) => {
  const p = (item && item.payload) || {};
  const rawConds = p.conditions;
  let conditions = [];
  if (Array.isArray(rawConds)) {
    conditions = rawConds
      .map((c) => (c && typeof c === "object" ? c : { name: String(c || ""), source: "free-text" }))
      .filter((c) => c.name && c.name.trim());
  } else if (typeof rawConds === "string" && rawConds.trim()) {
    conditions = rawConds
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name) => ({ name, source: "free-text" }));
  } else if (typeof p.condition === "string" && p.condition.trim()) {
    conditions = [{ name: p.condition.trim(), source: "free-text" }];
  }
  return {
    relativeId:    `staged-${item.id}`,
    relationship:  p.relationship || "other",
    customRelation: p.custom_relation || p.customRelation || "",
    conditions,
    description:   p.description || "",
    _captured:     item,
    _capturedIsAi: (item.confidence || 0) > 0,
    _stagedAdd:    true,
  };
};

const FamilyHistory = ({ patient, capturedItems, onRowsSync }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // relative → confirm dialog
  const [deletingId, setDeletingId] = useState(null);
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new FamilyHistoryService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { relatives: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load family history");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(FAM_V2_REFRESH, load);
    return () => EventService.removeListener(FAM_V2_REFRESH, load);
  }, [load]);

  const relativesRaw = useMemo(() => toRelativeList(doc && doc.relatives), [doc]);

  // Bubble the base v2 relative list up to the transcript modal so it
  // can dedup AI proposals against existing entries. When an AI-emitted
  // single-only relationship (father/mother/grandparent) already exists
  // the modal renders the proposal as an update card on that record
  // instead of a duplicate add.
  useEffect(() => {
    if (typeof onRowsSync === "function") onRowsSync(relativesRaw);
  }, [relativesRaw, onRowsSync]);

  // Transcript-mode overlay. Adds slot in as new peach rows; updates
  // annotate the matching existing relative with _captured so the
  // reviewer sees the peach highlight on the row they just modified.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const relatives = useMemo(() => {
    if (!inTranscriptMode) return relativesRaw;
    const stagedAdds = [];
    const updatesByEntryId = new Map();
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if (it.operation === "add") stagedAdds.push(stagedAddToRelativeRow(it));
      else if (it.operation === "update" && it.targetEntryId) {
        updatesByEntryId.set(String(it.targetEntryId), it);
      }
    }
    // Overlay staged payload fields onto the base row so the table
    // columns (Relationship / Conditions / Description) show what the
    // reviewer just saved, not the untouched FH v2 record.
    const annotated = relativesRaw.map((row) => {
      const update = updatesByEntryId.get(String(row.relativeId));
      if (!update) return row;
      const p = (update.payload) || {};
      return {
        ...row,
        relationship:   p.relationship   || row.relationship,
        customRelation: p.custom_relation !== undefined ? p.custom_relation : row.customRelation,
        conditions:     Array.isArray(p.conditions) ? p.conditions : row.conditions,
        description:    p.description !== undefined ? p.description : row.description,
        _captured:      update,
        _capturedIsAi:  (update.confidence || 0) > 0,
      };
    });
    return [...annotated, ...stagedAdds];
  }, [relativesRaw, capturedItems, inTranscriptMode]);
  // Soft-deleted rows now ride along on the same GET payload
  // (deletedRelatives). No separate fetch; the modal reads this list.
  const deletedRelatives = useMemo(
    () => toRelativeList(doc && doc.deletedRelatives),
    [doc]
  );

  // Actor display names are enriched by api-family-history-v2-service on
  // every list/mutation response — no per-page roster fetch needed here.
  // Table-order copy — canonical family-tree sequence (parents → maternal
  // grandparents → paternal grandparents → siblings → other). Insertion order
  // is preserved as the tiebreaker so Brother-1 / Brother-2 numbering stays
  // stable regardless of when each was added.
  const sortedRelatives = useMemo(
    () => sortRelativesByRelationship(relatives),
    [relatives]
  );

  const openAdd = () => EventService.emit(FAM_V2_ADD);
  // Transcript-mode captured rows route to a re-edit event so the modal
  // can pop the AI proposal back open as an editable card rather than
  // opening the standalone edit sheet in the patient-page rail.
  const openEdit = (r) => {
    if (r && r._captured) {
      EventService.emit(FAM_V2_EDIT_CAPTURED, {
        captured: r._captured,
        famv2Record: r._stagedAdd ? null : r,
      });
      return;
    }
    EventService.emit(FAM_V2_EDIT, { relative: r });
  };

  // Trash → the board's confirm dialog (3378:14407, identical to the shared
  // one bar its sentence); the wire call runs on its "Delete entry".
  const confirmDelete = () => {
    const r = deleteTarget;
    if (!r) return;
    setDeletingId(r.relativeId);
    const svc = new FamilyHistoryService();
    svc.delete(
      patientId,
      r.relativeId,
      (doc && doc.version) || 0,
      (res) => {
        setDoc(res || doc);
        setDeletingId(null);
        setDeleteTarget(null);
        EventService.emit(FAM_V2_REFRESH);
      },
      (err) => {
        setDeletingId(null);
        setDeleteTarget(null);
        if (err && err.reason === "version") load();
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Delete failed — please retry");
      }
    );
  };

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading family history…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="fh3-card">
      <header className="fh3-toolbar">
        <h2 className="fh3-title">Family history</h2>
        <span className="fh3-count">
          &middot; {relatives.length === 0 ? "No entries" : `${relatives.length} entries`}
        </span>
        {/* The Deleted-entries button is independent of the live list —
            deleting the LAST relative shouldn't strand the audit trail
            behind. Add-family-member stays gated on relatives.length so
            the empty board keeps its single body CTA (3071:2950). */}
        {(relatives.length > 0 || deletedRelatives.length > 0) && (
          <div className="fh3-actions">
            {/* Board 4064:3096. Live when the server reports any deleted
                relatives on the current doc; hidden otherwise so the
                toolbar isn't cluttered with a no-op button. */}
            {deletedRelatives.length > 0 && (
              <button
                type="button"
                className="fh3-btn fh3-btn-deleted"
                onClick={() => setShowDeletedModal(true)}
                title="View deleted entries"
              >
                <HistoryGlyph /> Deleted entries
                <span className="fh3-btn-count"> · {deletedRelatives.length}</span>
              </button>
            )}
            {relatives.length > 0 && (
              <button type="button" className="fh3-btn fh3-btn-add" onClick={openAdd}>
                <ToolbarPlusIcon /> Add family member
              </button>
            )}
          </div>
        )}
      </header>

      {relatives.length === 0 ? (
        <>
          {/* The empty board (3071:2950) keeps a thead, but only THREE
              columns — the audit pair and the actions slot are dropped
              along with the toolbar buttons. */}
          <div className="fh3-thead is-empty" role="row">
            <span role="columnheader">Relationship</span>
            <span role="columnheader">Condition</span>
            <span role="columnheader">Description</span>
          </div>
          <div className="fh3-empty">
            <span className="fh3-empty-medallion" aria-hidden="true"><FamilyMarkIcon /></span>
            <p className="fh3-empty-title">No family history recorded</p>
            <p className="fh3-empty-hint">
              Nothing has been logged yet. Add the patient&rsquo;s first relative to begin building their history.
            </p>
            <button type="button" className="fh3-empty-cta" onClick={openAdd}>
              <PlusIcon /> Add relative
            </button>
          </div>
        </>
      ) : (
        <div className="fh3-table" role="table">
          <div className="fh3-thead" role="row">
            <span role="columnheader">Relationship</span>
            <span role="columnheader">Condition</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Added by</span>
            <span role="columnheader">Added on</span>
            <span role="columnheader" aria-label="Actions" />
          </div>

          {sortedRelatives.map((r) => {
            // Pass the original (insertion-order) list so sibling numbering
            // is stable — the sort above only reorders rendering.
            // relationshipLabel falls back to the raw `relationship` value,
            // which is an object on some payloads — text-coerce it too.
            const label = asText(relationshipLabel(r, relatives));
            const desc = asText(r.description);
            const by = addedBy(r);
            const on = addedOn(r);
            return (
              <div
                className={`fh3-row${r._captured ? " is-captured" : ""}`}
                role="row"
                key={r.relativeId}
              >
                <span className="fh3-cell fh3-c-rel" role="cell">
                  {/* Captured (peach) rows stay clickable — openEdit
                      branches on _captured and routes to
                      FAM_V2_EDIT_CAPTURED so the modal can pop the AI
                      proposal back open as an editable card. */}
                  <button
                    type="button"
                    className="fh3-rel-btn"
                    onClick={() => openEdit(r)}
                    title={r._stagedAdd ? "Edit captured relative" : "Edit relative"}
                  >
                    {r._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                    {label}
                  </button>
                </span>
                <span className="fh3-cell fh3-c-cond" role="cell">
                  {/* 3-line clamp + white hover tooltip; only reveals
                      when the text is actually being truncated. Native
                      `title` dropped so the popover appearance matches
                      the rest of the fleet. */}
                  <MetaTooltip text={formatConditions(r.conditions)} lines={3} />
                </span>
                <span className="fh3-cell fh3-c-desc" role="cell">
                  <MetaTooltip text={desc || ""} lines={3} />
                </span>
                <span className="fh3-cell fh3-c-meta" role="cell">
                  {by || <Dash />}
                </span>
                <span className="fh3-cell fh3-c-meta" role="cell">
                  {on ? formatShortDate(on) : <Dash />}
                </span>
                <span className="fh3-cell fh3-c-actions" role="cell">
                  {!r._stagedAdd && (
                    <button
                      type="button"
                      className="fh3-del"
                      onClick={() => setDeleteTarget(r)}
                      disabled={deletingId === r.relativeId}
                      aria-label={`Delete ${label}`}
                      title="Delete"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          /* Board copy: "This removes {relation} — {condition} from the
             family history." The condition half is dropped when the entry
             has none, rather than printing a dangling dash. */
          name={
            formatConditions(deleteTarget.conditions) === "—"
              ? relationshipLabel(deleteTarget, relatives)
              : `${relationshipLabel(deleteTarget, relatives)} — ${formatConditions(deleteTarget.conditions)}`
          }
          listPhrase="the family history"
          submitting={deletingId === deleteTarget.relativeId}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {showDeletedModal && (
        <DeletedEntriesModal
          patient={patient}
          entries={deletedRelatives}
          onClose={() => setShowDeletedModal(false)}
        />
      )}
    </div>
  );
};

FamilyHistory.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
  /** Fires whenever the base v2 relative list changes. Transcript
   *  modal uses it to dedup AI proposals by relationship. */
  onRowsSync: PropTypes.func,
};

export default FamilyHistory;
